import { sortCurrencies } from './webledgerCurrencyOrder.js';
import {
  askConfirm, closeModalAndWait, currencyChipHtml, dateFieldHtml, escapeHtml, markCurrentRow, mountDateField,
  notchedFieldHtml, wireDayDrag,
  type MoneyCurrency,
} from '@colplat/ui-web';
import type { PresenceUser } from '@colplat/protocol';
import { apiFetch } from '../api.js';
import { appHref } from '../globals.js';
import { datasetLedger } from './webledgerLedger.js';
import {
  accountsView, attachmentsOf, bankInfoOf, buildAccountRegister, isTrue,
  type AccountRegisterFilters,
} from '@colplat/app-webledger/domain';
import { inlineAccountIcon } from './webledgerAccountDisplay.js';
import { accountPath } from './webledgerAccountTree.js';
import { creditLabel, debitLabel } from './webledgerLabels.js';
import type { AccountDto, CurrencyDto, TransactionDto } from './webledgerOfflineState.js';
import { PREF } from './webledgerPrefs.js';
import { attachmentTileHtml, type AttachmentDto } from './webledgerAttachments.js';
import { bankInfoBadgeHtml, mountBankInfo } from './webledgerBankInfo.js';
import { createColumnLayout } from './webledgerRegisterColumns.js';
import { restoreScroll, trackScroll, type ScrollTracker } from './webledgerScroll.js';
import { applyRecordState, recordState, RecordStateTracker } from './webledgerRecordState.js';
import { bsModal, openTransactionEditor, reopenDiscardedEditor, transactionEditorModalHtml, wireSplitHelp } from './webledgerTransactionEditor.js';
import {
  bootstrapWebledgerClient, clearPageNotice, pageFailure, webledgerShellHtml, wireBackButton,
  type WebledgerClient,
} from '../webledgerClient.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

/**
 * One account's register — legacy's `ledger.php`.
 *
 * The difference from the reports is what the rows are *about*. A report
 * lists transactions; a register lists what happened to **this account**, in
 * order, with a running balance after each one. That is why the debit and
 * credit headings are the account type's own words ("Increase"/"Decrease" for
 * an asset, "Income"/"Charge" for income) rather than the generic pair: the
 * whole page is written from this account's point of view.
 *
 * One row is one *transaction*, not one leg, because a transaction can touch
 * the same account twice — an in-account currency exchange does exactly that,
 * and the register labels it `exchange` since there is no counter account to
 * name.
 */

interface RegisterMovement { currencyCode: string; amount: string; running: string }
interface RegisterRow {
  transactionId: string; txDate: string; description: string; reference: string;
  memo: string; transfer: string; transferAccountId: string | null;
  movements: RegisterMovement[]; version: number; seq: number | null;
}
interface RegisterLeg {
  accountId: string; accountName: string; memo: string; value: string; amount: string; currencyCode: string;
}
interface RegisterResponse {
  account: AccountDto;
  opening: Record<string, string>;
  currencies: string[];
  balances: Record<string, string>;
  rows: RegisterRow[];
  splitsByTx: Record<string, RegisterLeg[]>;
  /** Attachment metadata for the shown rows, keyed by transaction — thumbnails inline, bytes never. */
  attachments?: Record<string, AttachmentDto[]>;
  /** Which of these rows came from a bank feed. Ids only; the payload is fetched on demand. */
  bankInfo?: string[];
  /**
   * Who holds each transaction on this page, as of the moment it was asked
   * for. Optional so a response cached before this existed still parses.
   */
  locks?: Array<{
    id: string;
    lockedBy?: PresenceUser | null;
    ownedBy?: PresenceUser | null;
    ownedSince?: string | null;
  }>;
  filterCurrency: string;
}

export async function mountWebledgerRegister(root: HTMLElement, accountId: string): Promise<void> {
  root.innerHTML = webledgerShellHtml('accounts', shellBodyHtml())
    + transactionEditorModalHtml()
    + '<div id="wlTimeline"></div>'
    + '<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>';
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('#wlRegHead')!);
  wireSplitHelp(root);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  const { token, prefs, dataset } = client;
  const fail = pageFailure(root, client.notify);

  const areaEl = root.querySelector<HTMLElement>('#wlRegArea')!;
  const headEl = root.querySelector<HTMLElement>('#wlRegHead')!;
  const formEl = root.querySelector<HTMLFormElement>('#wlRegFilters')!;
  const fromInput = formEl.elements.namedItem('from') as HTMLInputElement;
  const toInput = formEl.elements.namedItem('to') as HTMLInputElement;
  const currencySelect = formEl.elements.namedItem('currency') as HTMLSelectElement;
  const staleEl = root.querySelector<HTMLElement>('#wlRegStale')!;
  const splitsBtn = root.querySelector<HTMLButtonElement>('#wlRegSplits')!;
  const resetColsBtn = root.querySelector<HTMLButtonElement>('#wlRegResetCols')!;

  /*
   * The chart of accounts and the currency table, off the replica.
   *
   * They were two cached reads, which made them the only things on this page
   * that could not follow a change: the rows below have been computed from
   * the replica since ADR-0025, but the account's own name in the heading and
   * the symbol on every chip came from a response fetched once at mount. So a
   * rename in another window left this page titled with the old name until it
   * was reloaded.
   */
  let accounts: AccountDto[] = [];
  let currencyByCode = new Map<string, CurrencyDto>();
  const money = (code: string): MoneyCurrency | undefined => currencyByCode.get(code);

  /** The currency table as a list, for the editor, which takes one. */
  const currencies = (): CurrencyDto[] => [...currencyByCode.values()].sort((a, b) => a.code.localeCompare(b.code));

  /** Re-read the reference data. Synchronous — nothing is fetched. */
  function readVocabulary(): void {
    const ledger = datasetLedger(dataset);
    accounts = accountsView(ledger);
    currencyByCode = new Map(ledger.currencies()
      .map((c) => [c.code, {
        id: c.id, code: c.code, name: c.name, symbol: c.symbol ?? '',
        fraction: c.fraction ?? 2, useForTotals: Boolean(c.useForTotals),
      }] as const));
  }
  readVocabulary();

  // Written on load rather than on each link, so *every* way in counts — a
  // row link, a Transfer cell, a split line, the back button from an editor.
  prefs.set(PREF.lastAccount, accountId);

  let data: RegisterResponse | undefined;
  let showSplits = prefs.get(PREF.registerShowSplits) === '1';
  /** The transaction carrying the marker — see `markCurrentRow`. */
  let currentTxId: string | null = null;
  const scrollKey = PREF.scrollRegister(accountId);
  // Listening from here, recording only once the page has finished arriving.
  const scrollTracker: ScrollTracker = trackScroll(prefs, scrollKey);
  const records = new RecordStateTracker(client, () => paintRowStates());

  splitsBtn.setAttribute('aria-pressed', String(showSplits));


  async function load(): Promise<void> {
    areaEl.innerHTML = '<p class="text-secondary" role="status">Loading…</p>';
    // Emptying the table clamps the window to the top; that is not a scroll
    // the reader made, and saving it would lose their place.
    scrollTracker.settling();
    staleEl.hidden = true;
    // Separate from `staleEl`, which belongs to the offline state and is
    // cleared and re-set on every load: a failed reorder is not an offline
    // notice, and the two must not overwrite each other's meaning.
    clearPageNotice(root);
    draw();
  }

  /**
   * Recompute the register from the rows this device holds, and draw it.
   *
   * Split out of `load` so that a change arriving from elsewhere can redraw
   * without the things that belong to *asking* for a register: the "Loading…"
   * placeholder, the scroll reset, the notices. Somebody else's save should
   * change the numbers in front of you, not throw you back to the top of a
   * three-hundred-row page and blank it on the way.
   */
  function draw(): void {
    /*
     * Computed here, from the replica (ADR-0025).
     *
     * This was the last filter in the app still answered by the server, and
     * so the last one whose cache was keyed by its own query string — a date
     * range nobody had tried before returned nothing offline. `from`, `to`
     * and the leg-currency filter are the same functions `services/api` runs.
     */
    const ledger = datasetLedger(dataset);
    const account = ledger.accounts().find((a) => a.id === accountId);
    if (!account) {
      areaEl.innerHTML = '<p class="text-secondary">That account no longer exists.</p>';
      return;
    }
    const built = buildAccountRegister(ledger, accountId, currentFilters());
    data = {
      ...built,
      account: {
        id: account.id, name: account.name, type: account.type, currencyCode: account.currencyCode,
        isPlaceholder: isTrue(account.isPlaceholder), isMulticurrency: isTrue(account.isMulticurrency),
        iconPath: account.iconPath ?? '', parentId: account.parentId,
      },
      attachments: Object.fromEntries(built.rows.map((r) =>
        [r.transactionId, attachmentsOf(ledger, r.transactionId).map((a) => ({
          id: a.id, transactionId: r.transactionId, originalName: a.originalName, mime: a.mime,
          size: a.size ?? 0, thumb: a.thumb ?? '', created: null, url: '',
        }))])),
      bankInfo: built.rows
        .filter((r) => bankInfoOf(ledger, r.transactionId).length > 0)
        .map((r) => r.transactionId),
      // Echoed back the way the server used to, so the header can say which
      // currency the figures are narrowed to.
      filterCurrency: currentFilters().currency ?? '',
    };
    fromBank = new Set(data.bankInfo ?? []);
    const shown = data;
    records.seed(shown.rows.map((r) => ({ id: r.transactionId })));
    records.subscribe(shown.rows.map((r) => r.transactionId));
    renderHeader();
    renderRows();
    // Who holds these, and the signed links for the attachments: the two
    // things a device cannot know. Best-effort and after the paint, so the
    // register draws with no connection.
    void attachRowState(built.rows.map((r) => r.transactionId));
  }

  /*
   * Redraw whenever the rows move.
   *
   * `revision` rather than the bare notification: a delta usually carries
   * nothing and notifies anyway, and this page recomputes a whole account's
   * running balance and rebuilds its table. Doing that several times a
   * minute for no change would be felt on a long register.
   */
  let drawnAt = -1;
  dataset.subscribe(() => {
    if (dataset.revision() === drawnAt) return;
    drawnAt = dataset.revision();
    readVocabulary();
    draw();
  });

  /** The filter bar, as the domain reads it — no query string in between. */
  function currentFilters(): AccountRegisterFilters {
    return {
      ...(fromInput.value ? { from: fromInput.value } : {}),
      ...(toInput.value ? { to: toInput.value } : {}),
      ...(currencySelect.value ? { currency: currencySelect.value } : {}),
    };
  }

  /**
   * Who is holding each row — hold state on the server, and the only part of this page the
   * device cannot work out. Failing is fine: a register without lock marks
   * beats a register that would not draw.
   */
  async function attachRowState(ids: readonly string[]): Promise<void> {
    if (ids.length === 0) return;
    const res = await apiFetch('/apps/webledger/transactions/state', {
      token, method: 'POST', body: { ids: [...ids] },
    }).catch(() => undefined);
    if (!res?.ok) return;
    const body = (await res.json()) as { locks: Array<{ id: string }> };
    records.seed(body.locks);
  }

  function renderHeader(): void {
    if (!data) return;
    const account = data.account;
    const path = accounts.length > 0 ? accountPath(accounts, account.id) : account.name;
    const suffix = account.isMulticurrency ? 'multi-currency' : escapeHtml(account.currencyCode);
    headEl.innerHTML =
      `<div class="wl-reg-title"><span class="fw-semibold">${escapeHtml(path)}</span>`
      + `<span class="text-secondary small"> · ${suffix}</span></div>`
      + `<div class="cf-cur-chips">${sortCurrencies(Object.keys(data.balances)).map((code) => {
        const c = money(code);
        return currencyChipHtml(data!.balances[code]!, code, c ? { currency: c } : {});
      }).join('')}</div>`;

    // Offered only when there is a choice to make: a single-currency account
    // has nothing to filter, and a select with one option is furniture.
    const show = data.currencies.length > 1;
    currencySelect.closest('.col-auto')!.classList.toggle('d-none', !show);
    if (show) {
      // Only when the options would actually differ. This runs on every
      // redraw now, including ones nobody on this device asked for, and
      // reassigning `innerHTML` closes the list if the reader has it open
      // (invariant #5). Re-selecting the same value is a no-op; rebuilding
      // the same options is not.
      const options = '<option value="">All currencies</option>'
        + data.currencies.map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`).join('');
      if (currencySelect.innerHTML !== options) currencySelect.innerHTML = options;
      currencySelect.value = data.filterCurrency;
    }
  }

  /**
   * Draw the table, and say so out loud if that fails.
   *
   * Everything below is derived from a stored column layout, and a layout is
   * remembered — so a render that throws leaves an empty page that a reload
   * reproduces exactly. That is the worst shape a bug can take: no rows, no
   * message, and no reason to think anything can be done about it. If this
   * ever happens the reader gets a sentence and the one button that fixes it.
   */
  function renderRows(): void {
    try {
      renderRowsUnsafe();
    } catch (err) {
      console.error('[webledger] the register could not be drawn', err);
      areaEl.innerHTML = '<div class="alert alert-danger py-2 small mb-0">'
        + 'The register could not be drawn. If you have rearranged the columns, '
        + '<strong>Reset columns</strong> above will put them back.</div>';
      resetColsBtn.hidden = false;
    }
  }

  function renderRowsUnsafe(): void {
    if (!data) return;
    const account = data.account;
    const debit = debitLabel(account.type);
    const credit = creditLabel(account.type);

    if (data.rows.length === 0 && Object.keys(data.opening).length === 0) {
      areaEl.innerHTML = '<p class="text-secondary mb-0">No transactions in this period.</p>';
      return;
    }

    // The two amount headers name what a movement *means* for this account's
    // type, so they are read from the account on every render rather than
    // captured when the layout was built.
    labels = { debit, credit };

    const body = data.rows.map((row) => {
      const legs = showSplits ? (data!.splitsByTx[row.transactionId] ?? []) : [];
      return `<tr data-tx-row="${escapeHtml(row.transactionId)}" data-date="${escapeHtml(row.txDate)}" draggable="true">`
        + `${layout.rowCellsHtml(row)}</tr>`
        + legs.map((leg) => `<tr class="wl-reg-split">${layout.splitCellsHtml({ leg, txId: row.transactionId })}</tr>`).join('');
    }).join('');

    // The opening balance sits at the **bottom**: rows are newest-first, so
    // the earliest baseline is the last thing above the oldest row.
    const opening = Object.keys(data.opening).length > 0
      ? `<tr class="wl-reg-opening">${layout.spanRowCellsHtml('Opening balance', {
        balance: sortCurrencies(Object.keys(data.opening)).map((code) => {
          const c = money(code);
          return currencyChipHtml(data!.opening[code]!, code, c ? { currency: c } : {});
        }).join(''),
      })}</tr>`
      : '';

    areaEl.innerHTML = `<div class="cf-table-scroll"><table class="table cf-table align-middle wl-reg" style="${layout.tableStyle()}">
      ${layout.headHtml()}
      <tbody>${body}${opening}</tbody></table></div>`;
    paintRowStates();
    wireRowDrag();
    // Rebuilt with the table, because the header nodes it binds to are new
    // every time — and a reorder rebuilds the body too, since the cell order
    // is the column order.
    layout.wire(areaEl, (rerender) => {
      if (rerender) renderRows();
      // Both gestures store a layout, so both make the way back worth
      // offering — a resize that squeezed a column to nothing needs it as
      // much as a reorder does.
      resetColsBtn.hidden = !layout.isCustomised();
    });
    // Re-applied on every render: a filter change, a reorder and a live
    // refresh all rebuild this table, and losing your place to any of them
    // would be worse than never having had a marker.
    markCurrentRow(areaEl, currentTxId, { attr: 'data-tx-row' });
    scrollTracker.settling();
  }

  /** What a split-line cell is handed: the leg plus the transaction it belongs to. */
  interface SplitContext { leg: RegisterLeg; txId: string }
  /**
   * The two amount headers, refreshed from the account on every render.
   * They name what a movement *means* for this account's type, and the
   * column list is built once — so the labels live here rather than being
   * captured into it.
   */
  let labels = { debit: 'Debit', credit: 'Credit' };

  /**
   * Which rows carry a bank badge. A set rather than a flag on the row, so a
   * re-render after an edit reads the same answer the load did without the
   * row type having to carry it through every transform.
   */
  let fromBank = new Set<string>();

  /**
   * The register's columns: header, body cell and split-line cell together.
   *
   * One list rather than a header row plus a matching cell builder, because
   * the two were a pair that had to be kept in step by hand — and once the
   * order is the reader's to change, "kept in step by hand" stops being
   * possible at all.
   */
  const layout = createColumnLayout<RegisterRow, SplitContext>([
    {
      key: 'date', label: () => 'Date', cls: 'cf-date', width: 100,
      cell: (row) => `<span data-state-slot></span>${escapeHtml(row.txDate)}`,
    },
    {
      key: 'description', label: () => 'Description', cls: 'cf-title', width: 220,
      cell: (row) =>
        `<button type="button" data-open-tx="${escapeHtml(row.transactionId)}">`
        + `${escapeHtml(row.description || '(no description)')}</button>`
        + (row.reference ? `<span class="text-secondary small"> #${escapeHtml(row.reference)}</span>` : '')
        // In the description, as legacy has it: a bank origin is a fact about
        // this entry, not a column's worth of data.
        + (fromBank.has(row.transactionId) ? bankInfoBadgeHtml(row.transactionId) : ''),
      splitCell: ({ leg, txId }) => splitAccountHtml(leg, txId),
    },
    {
      key: 'memo', label: () => 'Memo', cls: 'text-secondary', width: 150,
      cell: (row) => escapeHtml(row.memo),
      splitCell: ({ leg }) => escapeHtml(leg.memo),
    },
    {
      key: 'transfer', label: () => 'Transfer', width: 190,
      cell: (row) => transferHtml(row),
    },
    {
      key: 'debit', label: () => labels.debit, cls: 'wl-num', width: 120,
      // One cell each for the two directions, filled by sign — which is what
      // makes a register scannable: everything that increased the account is
      // in one column and everything that reduced it in the other.
      cell: (row) => movementChips(row.movements.filter((m) => Number(m.amount) >= 0), false),
      splitCell: ({ leg }) => {
        const c = money(leg.currencyCode);
        return currencyChipHtml(leg.amount, leg.currencyCode, c ? { currency: c } : {});
      },
    },
    {
      key: 'credit', label: () => labels.credit, cls: 'wl-num', width: 120,
      cell: (row) => movementChips(row.movements.filter((m) => Number(m.amount) < 0), true),
    },
    {
      key: 'balance', label: () => 'Balance', cls: 'wl-num', width: 150,
      cell: (row) => row.movements.map((m) => {
        const c = money(m.currencyCode);
        return currencyChipHtml(m.running, m.currencyCode, c ? { currency: c } : {});
      }).join(''),
    },
    {
      // Legacy's `files` column, and the one this list was built to be able
      // to grow — a stored layout that predates it sorts it to the end
      // rather than losing it.
      key: 'files', label: () => 'Files', width: 96,
      cell: (row) => (data?.attachments?.[row.transactionId] ?? [])
        .map((file) => attachmentTileHtml(file)).join(''),
    },
  ], prefs);

  function movementChips(list: RegisterMovement[], absolute: boolean): string {
    return list.map((m) => {
      const c = money(m.currencyCode);
      const value = absolute ? String(-Number(m.amount)) : m.amount;
      return currencyChipHtml(value, m.currencyCode, c ? { currency: c } : {});
    }).join('');
  }

  /**
   * `?tx=` so the other side opens *on this transaction* rather than at the
   * top of a register you then have to search — legacy's Transfer link does
   * the same, and it is the difference between following a reference and
   * being handed a different list.
   */
  function transferHtml(row: RegisterRow): string {
    if (!row.transferAccountId) return `<span class="text-secondary">${escapeHtml(row.transfer)}</span>`;
    return `<a class="wl-acct" href="${escapeHtml(appHref(`/webledger/accounts/${row.transferAccountId}/register`)
      + `?tx=${encodeURIComponent(row.transactionId)}`)}" `
      + `title="${escapeHtml(accounts.length > 0 ? accountPath(accounts, row.transferAccountId) : row.transfer)}`
      + ` — open and point at this transaction">`
      + `${inlineAccountIcon(accounts, row.transferAccountId)}${escapeHtml(row.transfer)}</a>`;
  }

  /**
   * A split line's account: this account's own leg plain, every other one a
   * way into that account's register — which is how you follow money across
   * the book. `?tx=` again, because the question a split line raises is "what
   * does this look like from the other side", and that is a specific row.
   */
  function splitAccountHtml(leg: RegisterLeg, txId: string): string {
    const label = `${inlineAccountIcon(accounts, leg.accountId)}${escapeHtml(leg.accountName)}`;
    if (!data || leg.accountId === data.account.id) return `<span class="fw-semibold">${label}</span>`;
    return `<a class="wl-acct" href="${escapeHtml(appHref(`/webledger/accounts/${leg.accountId}/register`)
      + `?tx=${encodeURIComponent(txId)}`)}" `
      + `title="${escapeHtml(accounts.length > 0 ? accountPath(accounts, leg.accountId) : leg.accountName)}`
      + ` — open and point at this transaction">${label}</a>`;
  }

  function paintRowStates(): void {
    for (const tr of areaEl.querySelectorAll<HTMLElement>('[data-tx-row]')) {
      const id = tr.dataset['txRow']!;
      const holder = records.lockedBy.get(id);
      const state = recordState({
        tx: { id }, holder, owner: holder ? undefined : records.ownedBy.get(id),
        held: undefined, queuedEdit: false,
      });
      applyRecordState(tr, state);
    }
  }

  /**
   * Drag to reorder within one day — legacy's `wireRowDrag`.
   *
   * The gesture is `wireDayDrag`, shared with the wallet log. Crossing days
   * is refused there, during the drag: the date decides where a transaction
   * sits in the register and `seq` only orders what happens inside that day,
   * so letting a drag cross days would silently re-date a transaction.
   */
  function wireRowDrag(): void {
    wireDayDrag({
      container: areaEl,
      rowSelector: '[data-tx-row]',
      idOf: (row) => row.dataset['txRow'],
      dateOf: (row) => row.dataset['date'],
      onMove: (date, movedId, targetId) => { void applyReorder(date, movedId, targetId); },
    });
  }

  async function applyReorder(date: string, movedId: string, targetId: string): Promise<void> {
    if (!data || movedId === targetId) return;
    const sameDay = data.rows.filter((r) => r.txDate === date).map((r) => r.transactionId);
    const from = sameDay.indexOf(movedId);
    const to = sameDay.indexOf(targetId);
    if (from < 0 || to < 0) return;
    sameDay.splice(to, 0, ...sameDay.splice(from, 1));
    // Rows display newest-first while `seq` ascends with time, so the order
    // sent is the reverse of the order shown.
    const orderedIds = [...sameDay].reverse();
    const res = await apiFetch('/apps/webledger/transactions/reorder', {
      method: 'POST', token, body: { date, orderedIds },
    }).catch(() => undefined);
    // The rows are only repainted by `load()` below, so a failure here left
    // the register looking exactly as it did before the drag.
    if (!res?.ok) { fail('Could not save the new order.'); return; }
    await load();
  }

  formEl.addEventListener('submit', (event) => { event.preventDefault(); void load(); });
  // Our own date control rather than the browser's — see dateField.ts. Both
  // fields keep their names and their ISO values, so the filter code that
  // reads the form is untouched.
  for (const id of ['wlRegFrom', 'wlRegTo']) {
    mountDateField(
      root.querySelector<HTMLInputElement>(`#${id}`)!,
      root.querySelector<HTMLElement>(`[data-date-open="${id}"]`)!,
    );
  }
  currencySelect.addEventListener('change', () => { void load(); });
  // Delegated from the table's container, so rows re-rendered after an edit
  // keep working without re-wiring.
  mountBankInfo(areaEl, token, {
    localEntries: (txId) => bankInfoOf(datasetLedger(client.dataset), txId),
  });
  splitsBtn.addEventListener('click', () => {
    showSplits = !showSplits;
    splitsBtn.setAttribute('aria-pressed', String(showSplits));
    prefs.set(PREF.registerShowSplits, showSplits ? '1' : '0');
    renderRows();
  });
  areaEl.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    // Clicking a row moves the marker to it, so the tint says where *you*
    // were rather than where the app last decided to point. A currency chip
    // is its own gesture and says nothing about where the reader is.
    if (!target.closest('.cf-cur-chip')) {
      const row = target.closest<HTMLElement>('[data-tx-row]');
      if (row) {
        currentTxId = row.dataset['txRow'] ?? null;
        markCurrentRow(areaEl, currentTxId, { attr: 'data-tx-row' });
      }
    }
    const button = target.closest<HTMLElement>('[data-open-tx]');
    if (button) openRow(button.dataset['openTx']!);
  });
  areaEl.addEventListener('dblclick', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, input, select')) return;
    const row = target.closest<HTMLElement>('[data-tx-row]');
    if (row) openRow(row.dataset['txRow']!, { mark: true });
  });
  /**
   * Shown from here, not from `renderRows`.
   *
   * The whole point of this button is to rescue a layout that stops the table
   * rendering — so it cannot be revealed by the render. It is offered as soon
   * as a stored layout exists, whether or not anything below it works.
   */
  resetColsBtn.hidden = !layout.isCustomised();
  resetColsBtn.addEventListener('click', () => {
    layout.reset();
    resetColsBtn.hidden = true;
    renderRows();
    client.notify('Register columns are back to their original order and widths.');
  });
  wireAccountBulkDelete(
    root, client, accountId,
    () => (data ? (accounts.length > 0 ? accountPath(accounts, data.account.id) : data.account.name) : 'this account'),
    () => void load(),
  );
  const afterEditorSave = (savedId: string): void => {
    currentTxId = savedId;
    void load().then(() => markCurrentRow(areaEl, savedId, { attr: 'data-tx-row', flash: true, scroll: 'if-needed' }));
  };
  root.querySelector('#wlRegNewTx')?.addEventListener('click', () => {
    void openTransactionEditor(root, client, null, accounts, currencies(), afterEditorSave);
  });

  /**
   * The transactions this register is showing, in the order it shows them.
   *
   * Off the DOM, like the wallet log's: these rows are already this
   * account's, already past whatever the filters left, already in display
   * order and already one per transaction. A list rebuilt from the data
   * behind them would have to reproduce all four and would drift the first
   * time one of them changed.
   */
  function visibleTransactionIds(): string[] {
    const rows = areaEl.querySelectorAll<HTMLElement>('[data-tx-row]');
    return [...new Set([...rows].map((row) => row.dataset['txRow']).filter(
      (id): id is string => id !== undefined && id !== ''))];
  }

  const registerNeighbours = {
    idOf: (txId: string, delta: -1 | 1): string | undefined => {
      const ids = visibleTransactionIds();
      const at = ids.indexOf(txId);
      return at < 0 ? undefined : ids[at + delta];
    },
    go: (txId: string): void => { openRow(txId, { mark: true }); },
  };

  /**
   * Open a transaction, and point the table at it.
   *
   * The marking belongs here rather than at each caller, because every way
   * in wants it and only one of them was doing it: a single click marked the
   * row and then did not open it, a double click opened it and did not mark
   * it, and stepping with the arrows did both in the wrong order — the mark
   * went on before the fetch and the table was rebuilt underneath it.
   *
   * After the row is found, so a sweep is only asked for when there is
   * something to sweep.
   */
  function openRow(id: string, opts: { mark?: boolean } = {}): void {
    void (async () => {
      /*
       * Whatever is open goes first, and is waited for.
       *
       * Re-opening the editor on top of itself leaves the old instance's
       * handlers on the same static buttons, so the next click fires twice
       * against two different transactions — which is what stepping twice in
       * a row did: the first move was right and the second went nowhere.
       */
      await closeModalAndWait(
        root.querySelector<HTMLElement>('#txModal'),
        (el) => { bsModal(el)?.hide(); },
      );
      /*
       * Marked after the close, never before it. The outgoing editor's
       * teardown repaints this table, and a marker set first is wiped by the
       * repaint that follows it.
       */
      if (opts.mark) currentTxId = id;
      // The register's rows are a projection of this account's legs, not the
      // whole document, so the editor is handed the real record rather than
      // something reconstructed from what happens to be on screen.
      const res = await apiFetch(`/apps/webledger/transactions/${id}`, { token }).catch(() => undefined);
      // The editor modal never opens on this path, so without a visible
      // notice clicking the row simply appears to do nothing.
      if (!res?.ok) { fail('Could not open that transaction.'); return; }
      const { transaction } = (await res.json()) as { transaction: TransactionDto };
      void openTransactionEditor(root, client!, transaction, accounts, currencies(), (savedId) => {
        // Point back at what was just edited. `load()` rebuilds the table,
        // and `renderRows` re-applies the marker from `currentTxId` — this
        // only has to say which row that is and ask for the sweep.
        currentTxId = savedId;
        void load().then(() => markCurrentRow(areaEl, savedId, { attr: 'data-tx-row', flash: true, scroll: 'if-needed' }));
      }, { neighbours: registerNeighbours });
      /*
       * Marked once the editor is actually up, not before it.
       *
       * Opening one closes the last, and closing repaints this table — so a
       * marker set on the way in is wiped by a repaint that arrives after
       * it. `renderRows` does re-apply from `currentTxId`, which is why
       * stepping looked like it nearly worked; what it cannot do is run
       * again after the last of them. Asserting it here, when the form the
       * reader is looking at is the form that is open, is the point at which
       * it is true and stays true.
       */
      if (opts.mark) {
        markCurrentRow(areaEl, id, { attr: 'data-tx-row', flash: true, scroll: 'if-needed' });
      }
    })();
  }

  client.transport.onMessage((msg) => records.handleMessage(msg));
  client.transport.onStateChange((state) => {
    records.resetSubscriptions();
    if (state === 'open' && data) records.subscribe(data.rows.map((r) => r.transactionId));
  });

  await load();

  /**
   * Where to land.
   *
   * A `?tx=` on the URL is an explicit destination — someone followed a
   * Transfer link to see *this* transaction here — and it beats the
   * remembered position, which is only ever a guess about what you wanted.
   * It is centred rather than merely brought on screen, for the same
   * reason: being handed the row against the bottom edge of the window
   * answers the letter of "show me this" and not the point.
   * With no target, the remembered position is the better answer, and the
   * marker stays wherever it already was.
   *
   * If the target is not in the list the position wins instead. Legacy
   * clears its filters and reloads once at this point; there is nothing to
   * clear here, because the register's filters start empty on every page
   * load rather than being remembered, so a missing target means the
   * transaction is genuinely gone.
   */
  const targetTx = new URLSearchParams(window.location.search).get('tx');
  const landed = targetTx
    ? markCurrentRow(areaEl, targetTx, { attr: 'data-tx-row', flash: true, scroll: 'always' })
    : null;
  if (landed) currentTxId = targetTx;
  else await restoreScroll(prefs, scrollKey);
  scrollTracker.start();

  // An editor this tab had open when the browser discarded it, back with its
  // unsaved values (webledgerFormRestore.ts) — once the accounts it needs
  // have loaded.
  await reopenDiscardedEditor(root, client, accounts, currencies(), afterEditorSave);
}

/**
 * "Delete all in this account" — legacy's `account_delete_transactions`.
 *
 * A transaction is shared, so this removes each one *entirely*, including its
 * effect on every other account it touches. That is the only coherent
 * behaviour (half a transaction does not balance) and also the part nobody
 * expects, so the confirmation leads with it and the result reports how many
 * other accounts were caught.
 *
 * Offered only to an administrator, and only once the roles are known —
 * `bootstrapWebledgerClient` has already resolved them by the time this runs.
 * The server checks for itself regardless; hiding the button is about not
 * offering what will be refused, never about the refusal.
 */
function wireAccountBulkDelete(
  root: HTMLElement, client: WebledgerClient, accountId: string,
  label: () => string, onDeleted: () => void,
): void {
  const button = root.querySelector<HTMLButtonElement>('#wlRegDeleteTx');
  if (!button) return;
  if (!client.roles.includes('webledger-admin')) return;
  button.hidden = false;

  button.addEventListener('click', () => {
    void (async () => {
      const name = label();
      const go = await askConfirm({
        title: `Delete every transaction that touches “${name}”?`,
        message: [
          'Each one is removed in full — including its effect on the other accounts it touches, '
            + 'which may be accounts you did not mean to change.',
          'Deletion is soft: the rows stay in the database and anyone who can delete them can '
            + 'put them back from "Deleted records".',
          'Bank-imported transactions stay de-duplicated and will not come back on the next sync.',
        ],
        confirm: 'Delete them all', cancel: 'Keep them', tone: 'danger',
      });
      if (!go) return;

      button.disabled = true;
      const original = button.textContent;
      button.textContent = 'Deleting…';
      const res = await apiFetch(`/apps/webledger/accounts/${accountId}/delete-transactions`, {
        method: 'POST', token: client.token, body: {},
      }).catch(() => undefined);
      button.disabled = false;
      button.textContent = original;

      if (!res?.ok) {
        const body = res ? (await res.json().catch(() => ({}))) as { message?: string } : {};
        client.notify(body.message ?? 'Could not delete these transactions.');
        return;
      }
      const result = (await res.json()) as { transactions: number; otherAccounts: number };
      client.notify(
        `Deleted ${result.transactions} transaction${result.transactions === 1 ? '' : 's'}`
        + (result.otherAccounts > 0
          ? `, which also affected ${result.otherAccounts} other account${result.otherAccounts === 1 ? '' : 's'}.`
          : '.'),
      );
      onDeleted();
    })();
  });
}

function shellBodyHtml(): string {
  return `
    <div class="cf-card cf-card--bleed p-3">
      <div id="wlRegHead" class="wl-reg-head mb-2"></div>
      <form id="wlRegFilters" class="row g-2 align-items-end mb-2">
        <div class="col-auto wl-reg-date">
          ${notchedFieldHtml('From', dateFieldHtml('wlRegFrom', 'From', { name: 'from' }))}
        </div>
        <div class="col-auto wl-reg-date">
          ${notchedFieldHtml('To', dateFieldHtml('wlRegTo', 'To', { name: 'to' }))}
        </div>
        <div class="col-auto d-none">
          <label class="form-label mb-1" for="wlRegCur">Currency</label>
          <select class="form-select form-select-sm" name="currency" id="wlRegCur"></select>
        </div>
        <div class="col-auto"><button class="btn btn-sm btn-primary" type="submit">Run</button></div>
        <div class="col-auto"><button class="btn btn-sm btn-outline-secondary" type="button" id="wlRegSplits" aria-pressed="false">Show splits</button></div>
        <!-- Offered only once the columns have actually been rearranged. It
             is the way back from a layout you cannot fix by dragging, which
             a reload will not undo because the layout is remembered. -->
        <div class="col-auto"><button class="btn btn-sm btn-link px-1" type="button" id="wlRegResetCols" hidden>Reset columns</button></div>
        <div class="col-auto ms-auto"><button class="btn btn-sm btn-outline-secondary" type="button" id="wlRegNewTx">New transaction</button></div>
        <!-- Administrators only, and revealed after the roles are known
             rather than rendered and then hidden: a destructive button that
             flickers into view on every load is one somebody eventually
             clicks by accident. -->
        <div class="col-auto"><button class="btn btn-sm btn-outline-danger" type="button" id="wlRegDeleteTx" hidden>Delete all in this account</button></div>
      </form>
      <div class="alert alert-warning py-2 small" id="wlRegStale" role="status" hidden></div>
      <div id="wlRegArea"></div>
    </div>`;
}
